import { AsyncPipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { Validators } from '@angular/forms';
import { MatCard } from '@angular/material/card';
import { MatIcon } from '@angular/material/icon';
import { MatListOption, MatSelectionList } from '@angular/material/list';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { map } from 'rxjs';
import { AutoUnsubscribe } from '../../../core/auto-unsubscribe';
import { RequestTypeEnum } from '../../../shared/xatena-api/request-type';
import { RequestDto } from '../../../xatena-api';
import { DemandRequestDetailService } from '../demand-request-detail.service';
import { RequestTypeTestPositionComponent } from './request-type-test-position.component';

@AutoUnsubscribe()
@Component({
  selector: 'app-request-type-test-position-usz',
  templateUrl: './request-type-test-position-usz.component.html',
  styleUrls: ['./request-type-test-position-usz.component.scss'],
  imports: [
    MatCard,
    MatIcon,
    MatSelectionList,
    MatListOption,
    FormlyModule,
    AsyncPipe,
    TranslateModule,
  ],
})
export class RequestTypeTestPositionUszComponent
  extends RequestTypeTestPositionComponent
  implements OnInit
{
  public get isRequestSubmitTask$() {
    return this.demandRequestDetailService.activeTask$.pipe(
      map(t => t?.taskDefinitionKey === 'UserTask_09007dm')
    );
  }

  public get isSupervisorApprovalTask$() {
    return this.demandRequestDetailService.activeTask$.pipe(
      map(t => t?.taskDefinitionKey === 'supervisorApproval')
    );
  }

  constructor(
    translateService: TranslateService,
    demandRequestDetailService: DemandRequestDetailService
  ) {
    super(translateService, demandRequestDetailService);
  }
  protected updateTotalCost(request: RequestDto) {
    // NOOP
  }

  ngOnInit(): void {
    this.subs.sink = this.form.valueChanges.subscribe(s => {
      this.updateTotalCost(s);
    });

    this.subs.sink = this.demandRequestDetailService.requestProducts$.subscribe(
      requestProducts => {
        let costTotal = 0;
        let costTest = 0;
        if (requestProducts) {
          costTotal = requestProducts.reduce(
            (p, c) => p + +(c.amount ?? 0) * +(c.priceBaseUnit ?? 0),
            0
          );

          costTest = requestProducts.reduce(
            (p, c) => p + +(c.testAmount ?? 0) * +(c.priceBaseUnit ?? 0),
            0
          );
        }

        this.form.get('costTotal')?.setValue(costTotal, {
          emitEvent: false,
        });

        this.form.get('costTest')?.setValue(costTest, {
          emitEvent: false,
        });
      }
    );

    this.fields = [
      {
        hooks: {
          onInit: field => {
            this.subs.sink = this.isReadOnly$.subscribe(s => {
              if (s) {
                this.form.disable();
              } else {
                this.form.enable();
              }
            });
          },
        },
        hideExpression: model =>
          !(model.requestTypeId === RequestTypeEnum.TestPosition),
        fieldGroup: [
          {
            wrappers: ['mat-card'],
            templateOptions: {
              label: _('DEMAND_REQUEST.DETAIL.FORM.REQUEST_TYPE_7_GRP_1.LABEL'),
              translate: true,
            },
            fieldGroup: [
              {
                key: 'description',
                type: 'textarea',
                templateOptions: {
                  required: true,
                  appearance: this.formFieldAppearance,
                  label: _('DEMAND_REQUEST.DETAIL.FORM.DESCRIPTION.LABEL'),
                  description: _(
                    'DEMAND_REQUEST.DETAIL.FORM.DESCRIPTION.DESCRIPTION_RT7'
                  ),
                  translate: true,
                  autosize: true,
                  autosizeMinRows: 3,
                  autosizeMaxRows: 7,
                },
              },
              {
                key: 'reason',
                type: 'textarea',
                templateOptions: {
                  required: true,
                  appearance: this.formFieldAppearance,
                  label: _('DEMAND_REQUEST.DETAIL.FORM.REASON.LABEL'),

                  translate: true,
                  autosize: true,
                  autosizeMinRows: 3,
                  autosizeMaxRows: 7,
                },
              },
              {
                key: 'id',
                type: 'request-products-grid',
                templateOptions: {
                  label: _('DEMAND_REQUEST.DETAIL.FORM.PRODUCTS.LABEL'),
                  translate: true,
                  currency: this.currency$,
                  requestTypeId: this.requestTypeId$,
                  colDef: [
                    {
                      headerName: _(
                        'DEMAND_REQUEST.DETAIL.PRODUCTS.GRID.COL.TITLE'
                      ),
                      field: 'title',
                      flex: 1,
                    },
                    {
                      headerName: _(
                        'DEMAND_REQUEST.DETAIL.PRODUCTS.GRID.COL.SUPPLIER_NAME'
                      ),
                      field: 'supplierName',
                      flex: 1,
                    },
                    {
                      headerName: _(
                        'DEMAND_REQUEST.DETAIL.PRODUCTS.GRID.COL.SUPPLIER_ARTICLE_NUMBER'
                      ),
                      field: 'supplierArticleNumber',
                      flex: 1,
                    },
                    {
                      headerName: _(
                        'DEMAND_REQUEST.DETAIL.PRODUCTS.GRID.COL.TEST_AMOUNT'
                      ),
                      field: 'testAmount',
                      flex: 1,
                    },
                    {
                      headerName: _(
                        'DEMAND_REQUEST.DETAIL.PRODUCTS.GRID.COL.AMOUNT_RT7'
                      ),
                      field: 'amount',
                      flex: 1,
                    },
                  ],
                },
              },
              {
                key: 'costTest',
                type: 'currency',
                templateOptions: {
                  required: true,
                  readonly: true,
                  appearance: this.formFieldAppearance,
                  label: _('DEMAND_REQUEST.DETAIL.FORM.COST_TEST.LABEL'),
                  translate: true,
                  currency: this.currency$,
                },
              },
              {
                key: 'costTotal',
                type: 'currency',
                templateOptions: {
                  required: true,
                  readonly: true,
                  appearance: this.formFieldAppearance,
                  label: _('DEMAND_REQUEST.DETAIL.FORM.COST_TOTAL.LABEL_RT7'),
                  translate: true,
                  currency: this.currency$,
                },
                validators: {
                  validation: [Validators.min(0.05)],
                },
              },
              {
                key: 'procurementType',
                type: 'select',
                templateOptions: {
                  required: false,
                  appearance: this.formFieldAppearance,
                  label: _('DEMAND_REQUEST.DETAIL.FORM.PROCUREMENT_TYPE.LABEL'),
                  translate: true,
                  options: this.procurementTypeSelectList$,
                  valueProp: 'key',
                  labelProp: 'value',
                },
              },
              {
                key: 'location',
                type: 'input',
                templateOptions: {
                  required: false,
                  appearance: this.formFieldAppearance,
                  label: _('DEMAND_REQUEST.DETAIL.FORM.LOCATION.LABEL'),
                  translate: true,
                },
              },
              {
                key: 'testDuration',
                type: 'input',
                templateOptions: {
                  required: false,
                  appearance: this.formFieldAppearance,
                  label: _('DEMAND_REQUEST.DETAIL.FORM.TEST_DURATION.LABEL'),
                  translate: true,
                },
              },
              {
                key: 'usageByOthers',
                type: 'input',
                templateOptions: {
                  required: false,
                  appearance: this.formFieldAppearance,
                  label: _('DEMAND_REQUEST.DETAIL.FORM.USAGE_BY_OTHERS.LABEL'),
                  translate: true,
                },
              },
              {
                key: 'reusable',
                type: 'select',
                templateOptions: {
                  required: false,
                  appearance: this.formFieldAppearance,
                  label: _('DEMAND_REQUEST.DETAIL.FORM.REUSABLE.LABEL'),
                  translate: true,
                  options: this.reusableSelectList$,
                  valueProp: 'key',
                  labelProp: 'value',
                },
              },
              {
                key: 'replacing',
                type: 'textarea',
                templateOptions: {
                  required: true,
                  appearance: this.formFieldAppearance,
                  label: _('DEMAND_REQUEST.DETAIL.FORM.REPLACING.LABEL_RT7'),
                  description: _(
                    'DEMAND_REQUEST.DETAIL.FORM.REPLACING.DESCRIPTION_RT7'
                  ),
                  translate: true,
                  autosize: true,
                  autosizeMinRows: 3,
                  autosizeMaxRows: 7,
                },
              },
              {
                className: 'mat-hint-highlight',
                expressions: {
                  'templateOptions.description': (field: FormlyFieldConfig) => {
                    return field.model.benefitOutcome
                      ? this.translateService.instant(
                          _(
                            'DEMAND_REQUEST.DETAIL.FORM.BENEFIT_OUTCOME.DESCRIPTION'
                          )
                        )
                      : '';
                  },
                },
                key: 'benefitOutcome',
                type: 'checkbox',
                defaultValue: false,
                templateOptions: {
                  required: false,
                  appearance: this.formFieldAppearance,
                  label: _('DEMAND_REQUEST.DETAIL.FORM.BENEFIT_OUTCOME.LABEL'),
                  translate: true,
                },
              },
              {
                key: 'isMaterialCoveredByTariff',
                type: 'checkbox',
                defaultValue: false,
                templateOptions: {
                  required: false,
                  appearance: this.formFieldAppearance,
                  label: _(
                    'DEMAND_REQUEST.DETAIL.FORM.IS_MATERIAL_COVERED_BY_TARIFF.LABEL'
                  ),

                  translate: true,
                },
              },
              {
                expressions: {
                  hide: (field: FormlyFieldConfig) => {
                    return !field.model.isMaterialCoveredByTariff;
                  },
                },
                key: 'materialCoveredByTariff',
                type: 'input',
                templateOptions: {
                  required: true,
                  appearance: this.formFieldAppearance,
                  label: _(
                    'DEMAND_REQUEST.DETAIL.FORM.MATERIAL_COVERED_BY_TARIFF.LABEL'
                  ),
                  translate: true,
                },
              },
            ],
          },
        ],
      },
    ];
  }
}
